import { useQuery } from "@tanstack/react-query";
import { isNullOrUndefined } from "@utils/common";
import { queryClient } from "@/queryClient";
import { getMunicipalities, getSettlements, getStreets } from "./services";
import { type IMunicipality, type ISettlement, type IStreet } from "./types";

export const useMunicipalitiesQuery = (countryCode?: string) =>
  useQuery<IMunicipality[]>({
    queryKey: ["municipalities", countryCode],
    queryFn: () => getMunicipalities(countryCode),
  });

export const useSettlementsQuery = (
  municipalityId: IMunicipality["municipalityId"],
  enabled: boolean,
  countryCode?: string,
) =>
  useQuery<ISettlement[]>({
    queryKey: ["settlements", municipalityId, countryCode],
    queryFn: () => getSettlements(municipalityId, countryCode),
    enabled,
  });

export const useStreetsQuery = (
  municipalityId: IMunicipality["municipalityId"],
  settlementId: ISettlement["settlementId"],
  enabled: boolean,
  countryCode?: string,
) =>
  useQuery<IStreet[]>({
    queryKey: ["streets", municipalityId, settlementId, countryCode],
    queryFn: () => getStreets(municipalityId, settlementId, countryCode),
    enabled,
  });

export const prefetchAddress = (
  municipalityId?: IMunicipality["municipalityId"],
  settlementId?: ISettlement["settlementId"],
  countryCode?: string,
) =>
  Promise.all([
    queryClient.prefetchQuery({
      queryKey: ["municipalities", countryCode],
      queryFn: () => getMunicipalities(countryCode),
    }),
    ...(!isNullOrUndefined(municipalityId)
      ? [
          queryClient.prefetchQuery({
            queryKey: ["settlements", municipalityId, countryCode],
            queryFn: () => getSettlements(municipalityId, countryCode),
          }),
        ]
      : []),
    ...(!isNullOrUndefined(municipalityId) && !isNullOrUndefined(settlementId)
      ? [
          queryClient.prefetchQuery({
            queryKey: ["streets", municipalityId, settlementId, countryCode],
            queryFn: () =>
              getStreets(municipalityId, settlementId, countryCode),
          }),
        ]
      : []),
  ]);
